/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  SAMPLE_TABLE_SORTED_ROLES,
  TABLE_COLUMNS,
  TABLE_COLUMN_ID,
} from './BulkUserCreation.constants';
import { SAMPLE_DATA, SampleUserData } from './BulkUserCreationPrepareFile/SampleTable';

import { BaseRecord, TableColumn } from '@aircall/tractor-v2';
import { ROLE_NAME } from '@dashboard/library';
import { RoleInput } from '@generated/globalTypes';
import { UserFullFragment_roles } from '@generated/UserFullFragment';
import { RoleFlags } from '@helpers/roles.helpers';
import { TFunction } from 'i18next';

/**
 * Sorts the roles in the order retrieved from `SAMPLE_TABLE_SORTED_ROLES`.
 *
 * @param roles - The array of roles to be sorted.
 * @returns The sorted list of roles.
 */
export function getRolesInDesiredOrder(roles: UserFullFragment_roles[]): UserFullFragment_roles[] {
  return roles.sort((a, b) =>
    SAMPLE_TABLE_SORTED_ROLES.indexOf(a.name) < SAMPLE_TABLE_SORTED_ROLES.indexOf(b.name) ? -1 : 1
  );
}

/**
 * Get available roles based on user permissions.
 *
 * @param roleFlags - The user's role flags.
 * @param companyRoles - The array of all available roles.
 * @returns The array of available roles based on permissions.
 */
export function getRolesInput(
  { isAdmin, isOwner }: RoleFlags,
  companyRoles: UserFullFragment_roles[]
): RoleInput[] {
  /**
   * We filter out "Agent" which is irrelevant here.
   * The other roles are sorted in a certain way, as the backend relies on the order of the indexes.
   */
  let sortedRoles = getRolesInDesiredOrder(companyRoles);

  // If the user is not an admin, we remove the first two roles ("Supervisor" and "Admin")
  if (!isAdmin) {
    sortedRoles = sortedRoles.filter(
      (r) => r.name !== ROLE_NAME.SUPERVISOR && r.name !== ROLE_NAME.ADMIN
    );
  }

  // If the user is not an owner, we remove the last role ("Owner")
  if (!isOwner) {
    sortedRoles = sortedRoles.filter((r) => r.name !== ROLE_NAME.OWNER);
  }

  /**
   * If the user is admin + owner, we return all the roles
   * If the user is admin but not owner, we return the supervisor + admin roles
   * If the user is owner but not admin, we return the owner role
   * If the user doesn't have the proper roles, we return an empty array
   */
  return sortedRoles.map((r) => {
    // Convert AgentV2 role to RoleInput
    const { __typename, ...role } = r;
    return role;
  });
}

/**
 * Create CSV sample data based on user permissions.
 *
 * @param tableColumns - The array of table columns.
 * @param roleFlags - The user's role flags.
 * @returns The CSV sample data in a 2D array.
 */
export function createCsvSampleData(
  tableColumns: TableColumn<BaseRecord>[],
  { isAdmin, isOwner }: RoleFlags
): string[][] {
  const headers = tableColumns.map(({ label }) => label as string);
  const sampleData = SAMPLE_DATA.map(({ supervisor, admin, owner, ...rest }) => {
    let sampleUserData = { ...rest } as SampleUserData;

    if (isAdmin) {
      sampleUserData = { ...sampleUserData, supervisor, admin };
    }

    if (isOwner) {
      sampleUserData = { ...sampleUserData, owner };
    }

    return Object.values(sampleUserData);
  });

  return [headers, ...sampleData];
}

/**
 * Create sample table columns based on user permissions.
 *
 * @param roleFlags - The user's role flags.
 * @param t - The translation function.
 * @returns An array of columns to display in the sample table.
 */
export function createSampleTableColumns(
  { isAdmin, isOwner }: RoleFlags,
  t: TFunction
): TableColumn<BaseRecord>[] {
  const displayedColumns = Object.keys(TABLE_COLUMNS).filter((columnId) => {
    if (isAdmin && isOwner) {
      return true;
    }

    if (isAdmin) {
      return columnId !== TABLE_COLUMN_ID.OWNER;
    }

    if (isOwner) {
      return columnId !== TABLE_COLUMN_ID.ADMIN && columnId !== TABLE_COLUMN_ID.SUPERVISOR;
    }

    return false;
  });

  return displayedColumns.map((columnId) => ({
    id: columnId,
    label: t(TABLE_COLUMNS[columnId as TABLE_COLUMN_ID]),
  }));
}
